/**
 * SoilBoardPage — the soil workspace's landing surface (U1).
 *
 * Rows are products, columns are months, every cell is a session. Group counts
 * live in CELLS and never in the row header: grouping is a property of the
 * session, so the same product routinely has a different number of groups in
 * different months.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCard, KpiTile, Notice } from '../patterns';
import { soilProgramService, SoilBoard, SoilBoardCell } from '../../services/soilProgramService';
import { soilSessionService } from '../../services/soilSessionService';
import { summariseBoard } from './soilBoardSummary';
import { SoilWorkspaceContext } from './SoilWorkspaceLayout';
import { formatMeasure } from '../../utils/formatMeasure';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import './nutrition.css';

const CELL_CLASS: Record<string, string> = {
  draft: 'ntr-board-cell--draft',
  planned: 'ntr-board-cell--planned',
  in_progress: 'ntr-board-cell--progress',
  complete: 'ntr-board-cell--done',
  signed_off: 'ntr-board-cell--done',
};

const SoilBoardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { program } = useOutletContext<SoilWorkspaceContext>();
  const [board, setBoard] = useState<SoilBoard | null>(null);
  const [error, setError] = useState('');
  const [busyCell, setBusyCell] = useState('');

  const load = useCallback(async () => {
    if (!program) return;
    try {
      setBoard(await soilProgramService.getBoard(program._id));
      setError('');
    } catch (_err) {
      setError(t('nutrition.soil.board.loadFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?._id]);

  useEffect(() => { load(); }, [load]);

  // The layout's own trail (SoilWorkspaceLayout) does not re-run its effect on a
  // tab round trip (Board → Sign-off → Board): SoilSignOffPage clears the
  // crumbs on unmount and the layout's stable key is unchanged, so the top bar
  // goes empty. Registering the same trail here — a hook, so it must sit above
  // every early return below — keeps the Board tab's breadcrumb populated
  // regardless of layout re-render timing. The provider dedupes identical
  // trails by label/`to`, so this causes no churn against the layout's copy.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.soil.workspace.title') },
  ]);

  const cellAt = (month: string, productName: string): SoilBoardCell | undefined =>
    board?.cells.find((c) => c.month === month && c.productName === productName);

  const onCreate = async (month: string, productName: string) => {
    if (!program) return;
    setBusyCell(`${month}|${productName}`);
    try {
      const { created } = await soilSessionService.create(program._id, month, productName);
      // A freshly created session is always draft, so it opens the U2 grouping builder.
      if (created[0]?._id) navigate(`/nutrition/soil/sessions/${created[0]._id}/grouping`);
      else await load();
    } catch (_err) {
      setError(t('nutrition.soil.board.createFailed'));
      await load();
    } finally {
      setBusyCell('');
    }
  };

  // The error branch returns BEFORE the loading guard, so a failed load renders a
  // notice rather than sitting on "loading" forever.
  if (error && !board) {
    return <div data-testid="board-error"><Notice tone="danger">{error}</Notice></div>;
  }
  if (!program) return null;                     // layout renders the empty state
  if (!board) return <div>{t('common.loading')}</div>;

  const { kpi, attention } = summariseBoard(board);

  return (
    <div className="ntr-ws-body" data-testid="soil-board">
      {error && <Notice tone="danger">{error}</Notice>}

      <div className="pat-kpi-row">
        <KpiTile primary
          label={t('nutrition.soil.board.kpi.inFlight')}
          value={kpi.sessionsInFlight}
          delta={t('nutrition.soil.board.kpi.inFlightDelta', { inProgress: kpi.sessionsInProgress, planned: kpi.sessionsPlanned })} />
        <KpiTile alert={kpi.groupsAwaitingSignoff > 0}
          label={t('nutrition.soil.board.kpi.awaiting')}
          value={<span data-testid="board-kpi-awaiting">{kpi.groupsAwaitingSignoff}</span>}
          delta={kpi.oldestAwaitingDays === null
            ? undefined
            : t('nutrition.soil.board.kpi.awaitingDelta', { count: kpi.oldestAwaitingDays })} />
        <KpiTile
          label={t('nutrition.soil.board.kpi.applied')}
          value={`${formatMeasure(kpi.appliedThisMonthKg)} kg`}
          delta={t('nutrition.soil.board.kpi.appliedDelta', { planned: formatMeasure(kpi.plannedThisMonthKg) })} />
        <KpiTile
          label={t('nutrition.soil.board.kpi.unassigned')}
          value={kpi.unassignedOrchards}
          delta={t('nutrition.soil.board.kpi.unassignedDelta', { count: kpi.draftSessions })} />
      </div>

      <div className="ntr-board-attention">
        {attention.awaitingSignoff.length > 0 && (
          <div data-testid="board-attention-awaiting">
            <AlertCard tone="danger" count={attention.awaitingSignoff.reduce((s, a) => s + a.count, 0)}
              title={t('nutrition.soil.board.attention.awaiting')} onViewAll={() => navigate('/nutrition/soil/sign-off')}>
              <ul className="ntr-board-attention__list">
                {attention.awaitingSignoff.slice(0, 3).map((a) => (
                  <li key={a.sessionId}>{a.productName} · {a.month} — {a.count}</li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
        {attention.draftSessions.length > 0 && (
          <div data-testid="board-attention-draft">
            <AlertCard tone="warn" count={attention.draftSessions.length}
              title={t('nutrition.soil.board.attention.draft')}>
              <ul className="ntr-board-attention__list">
                {attention.draftSessions.slice(0, 3).map((d) => (
                  <li key={d.sessionId}>
                    <button type="button" className="ntr-board-attention__link"
                      onClick={() => navigate(`/nutrition/soil/sessions/${d.sessionId}/grouping`)}>
                      {d.productName} · {d.month} — {t('nutrition.soil.board.attention.unassigned', { count: d.unassignedOrchards })}
                    </button>
                  </li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
        {attention.uncreated.length > 0 && (
          <div data-testid="board-attention-uncreated">
            <AlertCard tone="info" count={attention.uncreated.length}
              title={t('nutrition.soil.board.attention.uncreated')}>
              <ul className="ntr-board-attention__list">
                {attention.uncreated.slice(0, 3).map((u) => (
                  <li key={u.month}>{u.month} — {t('nutrition.soil.board.attention.products', { count: u.productCount })}</li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
      </div>

      <div className="pat-card ntr-board-scroll">
        <table className="ntr-board">
          <thead>
            <tr>
              <th className="ntr-board__rowhead">{t('nutrition.soil.board.col.product')}</th>
              {board.months.map((m) => <th key={m}>{m}</th>)}
              <th className="ntr-board__season">{t('nutrition.soil.board.col.season')}</th>
            </tr>
          </thead>
          <tbody>
            {board.products.map((p) => (
              <tr key={p.productName}>
                <td className="ntr-board__rowhead" data-testid={`board-row-header-${p.productName}`}>
                  <span className="ntr-board__product">{p.productName}</span>
                  {p.analysis && (
                    <small className="ntr-board__analysis">
                      {`${p.analysis.n} – ${p.analysis.p} – ${p.analysis.k}`}
                    </small>
                  )}
                </td>
                {board.months.map((m) => {
                  const c = cellAt(m, p.productName);
                  const key = `${m}-${p.productName}`;
                  if (!c) {
                    return <td key={m}><span className="ntr-board-cell ntr-board-cell--none" aria-label={t('nutrition.soil.board.cell.none')}>—</span></td>;
                  }
                  if (!c.session) {
                    // A cell can exist in the programme with orchardCount 0 (every entry for
                    // this product×month pair has kg: 0 — soilBoardService only skips adding
                    // the orchard, not the pair). createSessionsForMonth then finds no
                    // orchards, skips the product, and returns created: []: the create button
                    // would be a silent no-op. Render the same non-interactive "not planned"
                    // treatment instead of a button that does nothing.
                    if (c.orchardCount === 0) {
                      return (
                        <td key={m}>
                          <span className="ntr-board-cell ntr-board-cell--none" data-testid={`board-empty-${key}`}
                            aria-label={t('nutrition.soil.board.cell.none')}>—</span>
                        </td>
                      );
                    }
                    return (
                      <td key={m}>
                        <button type="button" className="ntr-board-cell ntr-board-cell--create"
                          data-testid={`board-create-${key}`} disabled={busyCell === `${m}|${p.productName}`}
                          onClick={() => onCreate(m, p.productName)}>
                          {t('nutrition.soil.board.cell.create')}
                          <small>{t('nutrition.soil.board.cell.plan', { orchards: c.orchardCount, kg: formatMeasure(c.plannedKg) })}</small>
                        </button>
                      </td>
                    );
                  }
                  const s = c.session;
                  return (
                    <td key={m}>
                      <button type="button" className={`ntr-board-cell ${CELL_CLASS[s.status] || ''}`}
                        data-testid={`board-cell-${key}`}
                        onClick={() => navigate(s.status === 'draft'
                          ? `/nutrition/soil/sessions/${s._id}/grouping`
                          : `/nutrition/soil/sessions/${s._id}`)}>
                        {t(`nutrition.soil.board.status.${s.status}`, s.status)}
                        <em>{s.groupTotal === 0
                          ? t('nutrition.soil.board.cell.noGroups')
                          : `${s.groupComplete} / ${s.groupTotal} ${t('nutrition.soil.board.cell.groups')}`}</em>
                        <small>{t('nutrition.soil.board.cell.plan', { orchards: c.orchardCount, kg: formatMeasure(c.plannedKg) })}</small>
                      </button>
                    </td>
                  );
                })}
                <td className="ntr-board__season">{formatMeasure(p.seasonKg)}</td>
              </tr>
            ))}
            <tr className="ntr-board__totals">
              <td className="ntr-board__rowhead">{t('nutrition.soil.board.col.monthTotal')}</td>
              {board.months.map((m) => (
                <td key={m} data-testid={`board-month-total-${m}`}>
                  {formatMeasure(board.monthTotals.find((x) => x.month === m)?.plannedKg || 0)}
                </td>
              ))}
              <td className="ntr-board__season" data-testid="board-season-total">{formatMeasure(board.seasonKg)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SoilBoardPage;
